import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faMapMarkerAlt, faSearch, faCircleNotch, faCheckCircle, 
  faBuilding, faUndo 
} from '@fortawesome/free-solid-svg-icons';
import { API_BASE } from '../apiConfig';
import { useCalculatorData } from './useCalculatorData';

const goldColor = '#c5a059';

const UnifiedDemolitionERP = ({ onResultChange }) => {
  const { t } = useTranslation();
  const { meta, loading: loadingAssets } = useCalculatorData(2); 
  const { navbars, groups, assets } = meta;

  const [step, setStep] = useState(0);
  const [activeTab, setActiveTab] = useState(null); 
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [serverResult, setServerResult] = useState(null);

  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 380);

  useEffect(() => {
    const handleResize = () => setIsSmallScreen(window.innerWidth < 380);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [address, setAddress] = useState('');
  const [floor, setFloor] = useState('0'); 
  const [elevator, setElevator] = useState('none');
  const [selectedItems, setSelectedItems] = useState({});
  const [itemThickness, setItemThickness] = useState({});

  useEffect(() => {
    if (navbars && navbars.length > 0) {
      setActiveTab(navbars[0].id);
    }
  }, [navbars]);

  const groupedStructure = useMemo(() => {
    if (!navbars || !groups || !assets) return [];
    return navbars.map(nav => ({
      ...nav,
      childGroups: groups
        .filter(g => g.navbar_id === nav.id)
        .map(g => ({
          ...g,
          items: assets.filter(a => a.group_id === g.id)
        }))
    }));
  }, [navbars, groups, assets]);

  const visibleContent = useMemo(() => {
    if (searchQuery) {
      const lowerQuery = searchQuery.toLowerCase();
      return groupedStructure.map(nav => ({
        ...nav,
        childGroups: nav.childGroups.map(g => ({
          ...g,
          items: g.items.filter(i => i.name.toLowerCase().includes(lowerQuery))
        })).filter(g => g.items.length > 0)
      })).filter(nav => nav.childGroups.length > 0);
    }
    return groupedStructure.filter(nav => nav.id === activeTab);
  }, [searchQuery, activeTab, groupedStructure]);

  const handleUpdate = (id, val, hasThick) => {
    const num = Math.max(0, parseInt(val) || 0);
    setSelectedItems(prev => ({ ...prev, [id]: num }));
    if (num > 0 && hasThick && !itemThickness[id]) {
      setItemThickness(prev => ({ ...prev, [id]: "10" }));
    }
  };

  const calculate = async () => {
    const hasSelection = Object.values(selectedItems).some(v => v > 0);
    if (!hasSelection) return alert(t('select_work_alert'));

    setLoading(true);
    try {
        const cartForBackend = Object.entries(selectedItems)
            .filter(([_, qty]) => qty > 0)
            .map(([id, qty]) => {
                const asset = assets.find(a => String(a.id) === String(id));
                const itemName = asset ? asset.name : `Item #${id}`;
                return { id, qty, thick: itemThickness[id] || "5", name: itemName };
            });

        const res = await axios.post(`${API_BASE}/api/calculators/calculate`, {
            calculatorId: 2,
            address,
            floor: parseInt(floor) || 0,
            elevator,
            cart: cartForBackend
        });

        const { totalPrice, summary } = res.data;
        setServerResult({ total: totalPrice || 0, summary: summary || "" });

        if (onResultChange) {
            onResultChange(totalPrice, summary, {
                totalPrice,
                total_price: totalPrice,
                details: summary,
                category_name: 'DEMOLITION',
                location: address
            });
        }
        setStep(1);
    } catch (e) {
        alert(t('error'));
    } finally {
        setLoading(false);
    }
  };

  if (loadingAssets) return (
    <div style={{display:'flex', justifyContent:'center', padding:'100px'}}>
      <FontAwesomeIcon icon={faCircleNotch} spin size="2x" color={goldColor} />
    </div>
  );

  return (
    <div style={s.outerWrapper}>
      <div style={s.container}>
        {step === 0 ? (
          <div style={s.flexBody}>
            <div style={s.header}><h2 style={s.logoText}>LIBER <span style={{color: goldColor}}>DEMOLITION</span></h2></div>
            <div style={s.body}>
              <div style={s.sectionCard}>
                <div style={s.locationRow}>
                  <div style={{flex: 2}}>
                    <label style={s.label}>{t('address_label')}</label>
                    <div style={s.inputWrapper}>
                      <FontAwesomeIcon icon={faMapMarkerAlt} style={s.innerIcon}/>
                      <input placeholder={t('address_placeholder_house')} value={address} onChange={e=>setAddress(e.target.value)} style={s.minimalIn} />
                    </div>
                  </div>
                  <div style={{flex: 1}}>
                    <label style={s.label}>{t('floor_label')}</label>
                    <div style={s.inputWrapper}>
                      <FontAwesomeIcon icon={faBuilding} style={s.innerIcon}/>
                      <input type="number" value={floor} onChange={e=>setFloor(e.target.value)} style={s.minimalIn} />
                    </div>
                  </div>
                </div>
                <div style={s.segmentedControl}>
                  {[
                    {id:'none', t: t('elevator_none_short')},
                    {id:'small', t: t('elevator_small_short')},
                    {id:'cargo', t: t('elevator_large_short')}
                  ].map(item => (
                    <div key={item.id} onClick={()=>setElevator(item.id)} 
                      style={{...s.segment, background: elevator === item.id ? goldColor : 'transparent', color: elevator === item.id ? '#fff' : '#64748b'}}>
                      {item.t}
                    </div>
                  ))}
                </div>
              </div>

              <div style={s.searchBox}>
                <FontAwesomeIcon icon={faSearch} style={s.searchIcon} />
                <input placeholder={t('search_work')} style={s.searchIn} value={searchQuery} onChange={e=>setSearchQuery(e.target.value)} />
              </div>

              {!searchQuery && (
                <div style={s.navbar}>
                  {navbars.map(nav => (
                    <div key={nav.id} onClick={() => setActiveTab(nav.id)} 
                      style={{...s.navItem, color: activeTab === nav.id ? goldColor : '#94a3b8', borderBottom: activeTab === nav.id ? `3px solid ${goldColor}` : 'none'}}>
                      {nav.name}
                    </div>
                  ))}
                </div>
              )}

              <div style={s.itemsContainer}>
                {visibleContent.map(nav => nav.childGroups.map(group => (
                  <div key={group.id} style={s.goldGroupCard}>
                    <div style={s.categoryHeader}><span style={s.catName}>{group.name}</span></div>
                    <div style={{
                        ...s.universalGrid, 
                        gridTemplateColumns: isSmallScreen ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)' 
                    }}>
                      {group.items.map(item => {
                        const opts = JSON.parse(item.options || '{}');
                        return (
                          <div key={item.id} style={s.itemCard}>
                            <span style={{fontSize: '20px'}}>{item.icon || '🛠️'}</span>
                            <div style={s.itemName}>{item.name}</div>
                            {opts.hasThick ? (
                              <div style={s.thicknessBox}>
                                <label style={s.tinyLabel}>{t('cm')}</label>
                                <input type="number" value={itemThickness[item.id] || "10"} 
                                  onChange={(e) => setItemThickness({...itemThickness, [item.id]: e.target.value})} style={s.thicknessInput} />
                              </div>
                            ) : <div style={{height: '34px'}}></div>}
                            <div style={s.cardCounter}>
                              <button onClick={() => handleUpdate(item.id, (selectedItems[item.id] || 0) - 1, opts.hasThick)} style={s.qtyBtn}>-</button>
                              <input type="number" value={selectedItems[item.id] || 0} onChange={(e) => handleUpdate(item.id, e.target.value, opts.hasThick)} style={s.qtyInput} />
                              <button onClick={() => handleUpdate(item.id, (selectedItems[item.id] || 0) + 1, opts.hasThick)} style={s.qtyBtn}>+</button>
                            </div>
                            <div style={s.unitLabel}>{item.unit}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )))}
              </div>

              <div style={s.btnContainer}>
                <button style={s.mainBtn} onClick={calculate} disabled={loading}>
                  {loading ? <FontAwesomeIcon icon={faCircleNotch} spin /> : t('calculate_btn')}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div style={s.resultBody}>
             <div style={s.successHeader}><FontAwesomeIcon icon={faCheckCircle} size="3x" color="#10b981" /><h3 style={s.successTitle}>{t('calculation_ready')}</h3></div>
             <div style={s.finalCard}>
                <div style={s.priceBox}><span style={s.priceTitle}>{t('total_price_label')}</span><div style={s.priceText}>{serverResult.total.toLocaleString()} ֏</div></div>
                <div style={s.detailsList}>
                  <div style={s.detailRow}><span>{t('address_label')}:</span><b>{address || t('not_specified')}</b></div>
                  <div style={{...s.detailRow, flexDirection:'column', alignItems:'flex-start', border:'none'}}>
                    <span>{t('summary')}:</span><div style={s.summaryScroll}>{serverResult.summary}</div>
                  </div>
                </div>
             </div>
             <button style={s.undoBtn} onClick={() => setStep(0)}><FontAwesomeIcon icon={faUndo} /> {t('modify')}</button>
          </div>
        )}
      </div>
    </div>
  );
};

// ... Styles (նույնն են ինչ նախորդ պատասխանում)
const s = {
  outerWrapper: { display: 'flex', justifyContent: 'center', background: '#f1f5f9', height: '100dvh', width: '100%', overflow: 'hidden', boxSizing: 'border-box' },
  container: { width: '100%', maxWidth: '450px', height: '100%', background: '#fff', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxSizing: 'border-box' },
  header: { background: '#0f172a', padding: '15px', textAlign: 'center', flexShrink: 0 },
  logoText: { color: '#fff', margin: 0, fontSize: '14px', fontWeight: '900', letterSpacing: '2px' },
  body: { padding: '12px', display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', boxSizing: 'border-box' },
  flexBody: { display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' },
  sectionCard: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '12px', marginBottom: '10px', flexShrink: 0 },
  locationRow: { display: 'flex', gap: '8px', marginBottom: '8px' },
  label: { fontSize: '9px', fontWeight: '800', color: '#64748b', marginBottom: '4px', display: 'block' },
  inputWrapper: { position: 'relative', display: 'flex', alignItems: 'center' },
  innerIcon: { position: 'absolute', left: '10px', color: goldColor, fontSize: '12px' },
  minimalIn: { width: '100%', padding: '8px 8px 8px 30px', borderRadius: '8px', border: '1px solid #e2e8f0', outline: 'none', fontSize: '13px', boxSizing: 'border-box' },
  segmentedControl: { display: 'flex', background: '#f1f5f9', padding: '3px', borderRadius: '8px', flexShrink: 0 },
  segment: { flex: 1, textAlign: 'center', padding: '6px', borderRadius: '6px', fontSize: '11px', fontWeight: '700', cursor: 'pointer' },
  navbar: { display: 'flex', borderBottom: '1px solid #e2e8f0', marginBottom: '10px', overflowX: 'auto', flexShrink: 0, scrollbarWidth: 'none' },
  navItem: { padding: '10px 15px', fontSize: '10px', fontWeight: '800', cursor: 'pointer', textTransform: 'uppercase', whiteSpace: 'nowrap', flexShrink: 0 },
  searchBox: { position: 'relative', marginBottom: '10px', flexShrink: 0 },
  searchIn: { width: '100%', padding: '10px 10px 10px 35px', borderRadius: '10px', border: 'none', background: '#f1f5f9', fontSize: '12px', boxSizing: 'border-box' },
  searchIcon: { position: 'absolute', left: '12px', top: '12px', color: '#94a3b8' },
  itemsContainer: { flex: 1, overflowY: 'auto', paddingRight: '2px' },
  goldGroupCard: { marginBottom: '10px', background: '#fffcf5', borderRadius: '14px', border: `1px solid ${goldColor}33`, overflow: 'hidden' },
  categoryHeader: { display: 'flex', justifyContent: 'space-between', padding: '12px', alignItems: 'center' },
  catName: { fontWeight: '800', fontSize: '12px', color: '#1e293b' },
  universalGrid: { display: 'grid', gap: '8px', padding: '8px', background: '#fff', boxSizing: 'border-box' },
  itemCard: { border: '1px solid #f1f5f9', borderRadius: '12px', padding: '12px 6px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between', minHeight: '150px', boxSizing: 'border-box' },
  itemName: { fontSize: '10px', fontWeight: '700', color: '#475569', minHeight: '30px', display: 'flex', alignItems: 'center', lineHeight: '1.2' },
  thicknessBox: { width: '100%', marginBottom: '5px' },
  tinyLabel: { fontSize: '7px', color: goldColor, fontWeight: '700' },
  thicknessInput: { width: '90%', padding: '4px', borderRadius: '4px', border: '1px solid #e2e8f0', fontSize: '12px', textAlign: 'center' },
  cardCounter: { display: 'flex', alignItems: 'center', background: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0', width: '100%', justifyContent: 'center' },
  qtyBtn: { border: 'none', background: 'none', fontWeight: 'bold', color: goldColor, padding: '6px 10px', cursor: 'pointer', fontSize: '16px' },
  qtyInput: { width: '30px', border: 'none', background: 'transparent', textAlign: 'center', fontSize: '12px', fontWeight: '800' },
  unitLabel: { fontSize: '9px', color: '#94a3b8', marginTop: '5px' },
  btnContainer: { padding: '10px 0', flexShrink: 0 },
  mainBtn: { width: '100%', padding: '15px', borderRadius: '14px', border: 'none', background: '#0f172a', color: '#fff', fontWeight: '900', fontSize: '14px', cursor: 'pointer' },
  resultBody: { padding: '20px', display: 'flex', flexDirection: 'column', height: '100%', boxSizing: 'border-box' },
  successHeader: { textAlign: 'center', marginBottom: '20px' },
  successTitle: { fontSize: '18px', fontWeight: '800', marginTop: '10px' },
  finalCard: { background: '#fff', borderRadius: '24px', border: '1px solid #e2e8f0', overflow: 'hidden', marginBottom: '15px', flex: 1, display: 'flex', flexDirection: 'column' },
  priceBox: { background: '#0f172a', color: '#fff', padding: '20px', textAlign: 'center' },
  priceText: { fontSize: '28px', fontWeight: '900', color: '#10b981', marginTop: '5px' },
  detailsList: { padding: '15px', overflowY: 'auto', flex: 1 },
  detailRow: { display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f1f5f9' },
  summaryScroll: { fontSize: '13px', color: '#475569', whiteSpace: 'pre-line', marginTop: '10px', lineHeight: '1.4' },
  undoBtn: { width: '100%', padding: '15px', borderRadius: '14px', border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', fontWeight: '700', cursor: 'pointer' }
};

export default UnifiedDemolitionERP;